import { useState } from "react";
import dayjs from "dayjs";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flag, Sparkle, Check } from "@phosphor-icons/react";

export default function GoalPlanCard({ onApplied }) {
  const [form, setForm] = useState({ goal: "", target_date: "", days_per_week: 3 });
  const [plan, setPlan] = useState(null);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setPlan(null);
    setApplied(false);
    try {
      const { data } = await api.post("/calendar/goal-plan", form);
      setPlan(data.plan);
      setSummary(data.summary);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    const { data } = await api.post("/calendar/goal-plan/apply", { events: plan });
    toast.success(`Добавлено ${data.created} тренировок в календарь`);
    setApplied(true);
    onApplied?.();
  };

  return (
    <div className="glass-card p-5 mt-5" data-testid="goal-plan-card">
      <h3 className="text-sm font-semibold flex items-center gap-2 mb-1">
        <Flag size={16} weight="duotone" className="text-[#ffea00]" /> Планирование от цели
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Назовите цель — AI составит план тренировок до целевой даты с учётом вашей энергии по дням недели и занятости.
      </p>
      <form onSubmit={generate} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <Label className="text-xs">Цель</Label>
          <Input value={form.goal} onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))}
            placeholder="Пробежать полумарафон" required data-testid="goal-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">К дате</Label>
          <Input type="date" value={form.target_date} min={dayjs().add(7, "day").format("YYYY-MM-DD")}
            onChange={(e) => setForm((f) => ({ ...f, target_date: e.target.value }))} required data-testid="goal-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Трен./нед.</Label>
          <select value={form.days_per_week} onChange={(e) => setForm((f) => ({ ...f, days_per_week: +e.target.value }))}
            className="h-9 rounded-md border border-input bg-secondary px-2 text-sm w-20" data-testid="goal-days-select">
            {[2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <Button type="submit" disabled={busy} data-testid="goal-generate-button">
          <Sparkle size={14} className="mr-1.5" /> {busy ? "Составляем…" : "Составить план"}
        </Button>
      </form>

      {plan && (
        <div className="mt-4" data-testid="goal-plan-preview">
          {summary && <p className="text-xs text-muted-foreground mb-3">{summary}</p>}
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {plan.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-sm p-2 rounded-md bg-white/[0.03] border border-white/[0.05]" data-testid={`goal-plan-item-${i}`}>
                <span className="text-xs text-muted-foreground num w-24 shrink-0 capitalize">{dayjs(p.date).format("dd, D MMM")}</span>
                <span className="text-xs text-muted-foreground num w-12 shrink-0">{p.time || "—"}</span>
                <span className="truncate">{p.title}</span>
              </div>
            ))}
          </div>
          <Button onClick={apply} disabled={applied} className="w-full mt-3" data-testid="goal-apply-button">
            {applied ? <><Check size={14} className="mr-1.5" /> Добавлено в календарь</> : `Добавить все ${plan.length} тренировок в календарь`}
          </Button>
        </div>
      )}
    </div>
  );
}
